"use client";

import rippleEffect from "@/utils/rippleEffect";
import Link from "next/link";
import { FC } from "react";
import styles from "./style.module.scss";
import MobileTradingViewChart from "@/components/coins/single/Overview/MobileTradingView";
import { useHistoricalPrice } from "@/hooks/useHistoricalPrice";
import MobileCalculator from "../Calculator/MobileCalculator";
import Image from "next/image";
import { CryptoCurrency } from "@/service/queries/cryptoCurrencies";
import { convertToDecimal } from "@/utils/convertToDecimal";

const timeButtonsList = ["24 ساعت", "1 هفته", " 1 ماه", " 1 سال"];

const OverviewMobile: FC<{ data: CryptoCurrency; coin: string }> = ({
  data,
  coin,
}) => {
  const historicalPriceHook = useHistoricalPrice(
    data?.crypto_currency_historical_price_list,
  );

  return (
    <div className={`${styles.overall} ${styles.small}`}>
      <div className={styles.first}>
        <span className={styles.title}>
          <span className={styles.name}>
            <Image
              src={`${process.env.APP_STORAGE_URL}/media/static/currency/logos/${coin}/${coin}.png`}
              alt="لوگو"
              loading="lazy"
              width={36}
              height={36}
            />
            <span>
              {data?.crypto_currency_name}
              <span>/TMN</span>
            </span>
            <i
              className={`${
                convertToDecimal(data?.daily_change_percentage).gte(0)
                  ? styles.increaseColor
                  : styles.decreaseColor
              }`}
            >
              {convertToDecimal(data?.daily_change_percentage)
                .dividedBy(100)
                .toNumber()
                .toLocaleString("en-US", {
                  style: "percent",
                  maximumFractionDigits: 2,
                })}
            </i>
          </span>

          <strong
            className={`
              ${
                convertToDecimal(data?.daily_change_percentage).gte(0)
                  ? styles.increaseColor
                  : styles.decreaseColor
              }
            `}
          >
            {data?.price_in_irr
              ? (Number(data?.price_in_irr) / 10).toLocaleString("en-US", {
                  useGrouping: true,
                })
              : "-"}
          </strong>
        </span>

        {coin.toLowerCase() !== "hmstr" && coin.toLowerCase() !== "dogs" && (
          <div className={styles.tradingview}>
            {coin.toLowerCase() !== "usdt" ? (
              <MobileTradingViewChart
                quoteCurrency="USDT"
                baseCurrency={coin}
              />
            ) : (
              <iframe
                src={`${process.env.APP_CHART_URL}/USDT-TMN`}
                style={{ width: "100%", height: "100%" }}
                allowFullScreen
                loading="eager"
              />
            )}
          </div>
        )}

        <div className={styles.limits}>
          <div className={styles.progress}>
            <dl>
              <dt>پایین‌ترین قیمت خرید</dt>
              <dd>
                {historicalPriceHook.coinDataBaseOnTime?.min_irr_price
                  ? (
                      Number(
                        historicalPriceHook.coinDataBaseOnTime?.min_irr_price,
                      ) / 10
                    ).toLocaleString("en-US", { useGrouping: true })
                  : "-"}
              </dd>
              <dt>بالاترین قیمت</dt>
              <dd>
                {historicalPriceHook.coinDataBaseOnTime?.max_irr_price
                  ? (
                      Number(
                        historicalPriceHook.coinDataBaseOnTime?.max_irr_price,
                      ) / 10
                    ).toLocaleString("en-US", { useGrouping: true })
                  : "-"}
              </dd>
            </dl>
            <span
              role="progressbar"
              aria-valuemax={Number(
                historicalPriceHook.coinDataBaseOnTime?.max_irr_price,
              )}
              aria-valuemin={Number(
                historicalPriceHook.coinDataBaseOnTime?.min_irr_price,
              )}
              aria-valuenow={Number(data?.price_in_irr)}
            >
              <span
                role="presentation"
                style={{
                  left: `${
                    ((Number(data?.price_in_irr) -
                      Number(
                        historicalPriceHook.coinDataBaseOnTime?.min_irr_price,
                      )) /
                      (Number(
                        historicalPriceHook.coinDataBaseOnTime?.max_irr_price,
                      ) -
                        Number(
                          historicalPriceHook.coinDataBaseOnTime?.min_irr_price,
                        ))) *
                    100
                  }%`,
                }}
              ></span>
            </span>
          </div>

          <div className={styles.timeButtons}>
            {timeButtonsList.map((item, index) => (
              <div
                key={item}
                className={`${styles.timeButtonItem} ${
                  historicalPriceHook.activeTime === index
                    ? styles.timeButtonItemActive
                    : ""
                }`}
                onClick={() => historicalPriceHook.changeActiveTime(index)}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      <dl className={styles.second}>
        <dt>حجم معامله 24</dt>
        <dd>
          {data?.trading_value
            ? Number(data?.trading_value).toLocaleString("en-US", {
                useGrouping: true,
              })
            : "-"}
        </dd>
        <dt>حجم کل</dt>
        <dd>
          {data?.market_cap
            ? Number(data?.market_cap).toLocaleString("en-US", {
                useGrouping: true,
              })
            : "-"}
        </dd>
        <dt>تسلط به بازار (دامیننس)</dt>
        <dd>{data?.dominance}%</dd>
        <dt>مجموع ارز در دسترس</dt>
        <dd>
          {data?.total_supply
            ? Number(data?.total_supply).toLocaleString("en-US", {
                useGrouping: true,
              })
            : "-"}
        </dd>
        <dt>ارز در گردش</dt>
        <dd>
          {data?.circulating_supply
            ? Number(data?.circulating_supply).toLocaleString("en-US", {
                useGrouping: true,
              })
            : "-"}
        </dd>
        <dt>حداکثر ارز قابل عرضه</dt>
        <dd>
          {convertToDecimal(data?.max_supply)
            .toNumber()
            .toLocaleString("en-US", { useGrouping: true })}
          -
        </dd>
      </dl>

      <Link href={`/`} onTouchStart={rippleEffect}>
        مشاهده تحلیل {data?.persian_name}
      </Link>
      <MobileCalculator data={data} coin={coin} />
    </div>
  );
};

export default OverviewMobile;
